import React from "react";
import {
  Collapse,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import OrderHistoryOrderInfoCard from "@/features/my-order/features/order-history/components/order-info-section/OrderHistoryOrderInfoCard";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useOrderHistoryDetail from "@/features/my-order/features/order-history/hooks/useOrderHistoryDetail";
import { formatDateToYMDEGeneral } from "@/shared/utils";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import { getOrderHistoryRenderStatus } from "@/features/my-order/features/order-history/utils";
import { useSearchParams } from "react-router-dom";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";

function OrderHistoryOrderInfoContent() {
  const [searchParams] = useSearchParams();
  const { data, orderSheetSetId } = useOrderHistoryDetail();
  const { i18n, t } = useI18nHelper();
  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();
  const { navigateToBuyerOrderSheetSetPage, navigate } = useRouterHelper();
  const status = searchParams.get("status");
  const isKor = i18n.language === "ko";

  const getOrderDate = () => {
    const resubmittedAt = data?.resubmitted_at;
    const submittedAt = data?.submitted_at;

    if (!resubmittedAt && !submittedAt) return "";

    if (resubmittedAt) {
      return `${formatDateToYMDEGeneral(resubmittedAt, isKor, "-")} ${t(
        "modified"
      )}`;
    }

    return `${formatDateToYMDEGeneral(submittedAt!, isKor, "-")} ${t(
      "submitted"
    )}`;
  };

  const orderDate = getOrderDate();

  const compressedFileId = data?.compressed_exported_file?.id;
  const { ORDER_SHEET, CONFIRMED } = getOrderHistoryRenderStatus(data);
  const confirmedDate = `${formatDateToYMDEGeneral(
    data?.buyer_confirmed_at ?? data?.confirmed_at ?? "",
    isKor,
    "-"
  )} ${t(data?.buyer_confirmed_at ? "confirmed" : "publish")}`;

  return (
    <Flex flexDirection={"column"} gap={"16px"}>
      <Flex gap={"16px"}>
        {ORDER_SHEET ? (
          <OrderHistoryOrderInfoCard
            variant={"orderSheet"}
            date={orderDate}
            totalCount={`${data?.ordered_quantity}`}
            totalAmount={data?.ordered_amount ?? 0}
            currency={data?.line_sheet_set?.currency}
            onClick={() => {
              if (!orderSheetSetId || !data?.line_sheet_set.id) return;
              navigateToBuyerOrderSheetSetPage(
                +orderSheetSetId,
                data.line_sheet_set.id
              );
            }}
          />
        ) : (
          <OrderHistoryOrderInfoCard
            variant={"orderForm"}
            date={orderDate}
            totalCount={`${data?.ordered_quantity}`}
            totalAmount={data?.ordered_amount ?? 0}
            currency={data?.line_sheet_set?.currency}
            onClick={() => {
              if (!compressedFileId) return;
              fireDownloadUnoFile({ unoFileId: compressedFileId }, {});
            }}
          />
        )}
        {CONFIRMED && (
          <OrderHistoryOrderInfoCard
            variant={"confirm"}
            date={confirmedDate}
            totalCount={`${data?.confirmed_quantity}`}
            totalAmount={data?.confirmed_amount ?? 0}
            currency={data?.line_sheet_set?.currency}
            onClick={() => {
              navigate(
                `/order-sheet-sets/order-history/confirm-detail?order_sheet_set_id=${orderSheetSetId}&status=${
                  data?.status ?? status
                }&title=${data?.line_sheet_set?.id} ${
                  data?.line_sheet_set?.name
                }&line_sheet_set.type__eq=${data?.line_sheet_set?.type}`
              );
            }}
          />
        )}
      </Flex>
      {data?.statistic_by_brand &&
        Object.keys(data.statistic_by_brand).length !== 0 && (
          <OrderHistoryOrderInfoPerBrand />
        )}
    </Flex>
  );
}

export default OrderHistoryOrderInfoContent;

const OrderHistoryOrderInfoPerBrand = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { data } = useOrderHistoryDetail();
  // 브랜드별 금액은 라인시트세트 통화 그대로다(원화 라인시트면 원화)
  const currency = data?.line_sheet_set?.currency ?? "EUR";
  const brandList = Object.entries(data?.statistic_by_brand ?? {});
  const { CONFIRMED } = getOrderHistoryRenderStatus(data);
  const { tCurrencyString, tTitle, i18n } = useI18nHelper();

  const total = brandList.reduce(
    (
      acc,
      [
        ,
        {
          ordered_quantity,
          ordered_amount,
          confirmed_quantity,
          confirmed_amount,
        },
      ]
    ) => {
      acc.orderedQty += ordered_quantity ?? 0;
      acc.orderedAmt += ordered_amount ?? 0;
      acc.confirmedQty += confirmed_quantity ?? 0;
      acc.confirmedAmt += confirmed_amount ?? 0;
      return acc;
    },
    { orderedQty: 0, orderedAmt: 0, confirmedQty: 0, confirmedAmt: 0 }
  );
  const isKor = i18n.language === "ko";

  return (
    <Flex justifyContent={"center"} flexDirection={"column"} gap={"16px"}>
      <Collapse in={isOpen} animateOpacity>
        <Flex flexDirection={"column"} gap={"12px"}>
          <Text textStyle={"body-md-bold"} color={"#444444"}>
            {tTitle("order_history:brand_per_info.title")}
          </Text>
          <TableContainer border={"1px solid #D4D4D1"} borderRadius={"6px"}>
            <Table>
              <Thead background={"#FCFCFB"}>
                <Tr>
                  <CustomTh>{tTitle("brand")}</CustomTh>
                  <CustomTh textAlign={"center"}>
                    {`${tTitle("order_history:quantity")}(${tTitle(
                      "order_literal"
                    )}${CONFIRMED ? "/" + tTitle("confirmed") : ""})`}
                  </CustomTh>
                  <CustomTh textAlign={"center"}>
                    {`${tTitle("amount")}(${tTitle("order_literal")}${
                      CONFIRMED ? "/" + tTitle("confirmed") : ""
                    })`}
                  </CustomTh>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <CustomTd boxShadow={"0.5px 0 0 0 #D4D4D1"}>
                    <Text textStyle={"body-sm-medium"} color={"#444444"}>
                      {tTitle("total")}
                    </Text>
                  </CustomTd>
                  <CustomTd
                    boxShadow={"0.5px 0 0 0 #D4D4D1"}
                    textAlign={"center"}
                  >
                    <Flex
                      gap={"4px"}
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <Text
                        textStyle={
                          CONFIRMED ? "body-sm-regular" : "body-sm-medium"
                        }
                        color={CONFIRMED ? "#6F6F6C" : "#1272EF"}
                      >
                        {total.orderedQty}
                        {isKor ? "개" : ""}
                      </Text>
                      {CONFIRMED && (
                        <>
                          <Text
                            textStyle={"body-sm-regular"}
                            color={"#6F6F6C"}
                            lineHeight={1}
                          >
                            →
                          </Text>
                          <Text textStyle={"body-sm-medium"} color={"#1272EF"}>
                            {total.confirmedQty}
                            {isKor ? "개" : ""}
                          </Text>
                        </>
                      )}
                    </Flex>
                  </CustomTd>
                  <CustomTd textAlign={"center"}>
                    <Flex
                      gap={"4px"}
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <Text
                        textStyle={
                          CONFIRMED ? "body-sm-regular" : "body-sm-medium"
                        }
                        color={CONFIRMED ? "#6F6F6C" : "#1272EF"}
                      >
                        {tCurrencyString(total.orderedAmt, {
                            type: "SYMBOL",
                            code: currency,
                          })}
                      </Text>
                      {CONFIRMED && (
                        <>
                          <Text
                            textStyle={"body-sm-regular"}
                            color={"#6F6F6C"}
                            lineHeight={1}
                          >
                            →
                          </Text>
                          <Text textStyle={"body-sm-medium"} color={"#1272EF"}>
                            {tCurrencyString(total.confirmedAmt, {
                            type: "SYMBOL",
                            code: currency,
                          })}
                          </Text>
                        </>
                      )}
                    </Flex>
                  </CustomTd>
                </Tr>
                {brandList.map(([brand, stat], i) => (
                  <Tr key={i}>
                    <CustomTd boxShadow={"0.5px 0 0 0 #D4D4D1"}>
                      <Text textStyle={"body-sm-medium"} color={"#444444"}>
                        {brand}
                      </Text>
                    </CustomTd>
                    <CustomTd
                      boxShadow={"0.5px 0 0 0 #D4D4D1"}
                      textAlign={"center"}
                    >
                      <Flex
                        gap={"4px"}
                        justifyContent={"center"}
                        alignItems={"center"}
                      >
                        <Text
                          textStyle={
                            CONFIRMED ? "body-sm-regular" : "body-sm-medium"
                          }
                          color={CONFIRMED ? "#6F6F6C" : "#444444"}
                        >
                          {stat.ordered_quantity}
                          {isKor ? "개" : ""}
                        </Text>
                        {CONFIRMED && (
                          <>
                            <Text
                              textStyle={"body-sm-regular"}
                              color={"#6F6F6C"}
                              lineHeight={1}
                            >
                              →
                            </Text>
                            <Text
                              textStyle={"body-sm-medium"}
                              color={"#444444"}
                            >
                              {stat.confirmed_quantity}
                              {isKor ? "개" : ""}
                            </Text>
                          </>
                        )}
                      </Flex>
                    </CustomTd>
                    <CustomTd textAlign={"center"}>
                      <Flex
                        gap={"4px"}
                        justifyContent={"center"}
                        alignItems={"center"}
                      >
                        <Text
                          textStyle={
                            CONFIRMED ? "body-sm-regular" : "body-sm-medium"
                          }
                          color={CONFIRMED ? "#6F6F6C" : "#444444"}
                        >
                          {tCurrencyString(stat.ordered_amount, {
                            type: "SYMBOL",
                            code: currency,
                          })}
                        </Text>
                        {CONFIRMED && (
                          <>
                            <Text
                              textStyle={"body-sm-regular"}
                              color={"#6F6F6C"}
                              lineHeight={1}
                            >
                              →
                            </Text>
                            <Text
                              textStyle={"body-sm-medium"}
                              color={"#444444"}
                            >
                              {tCurrencyString(stat.confirmed_amount, {
                            type: "SYMBOL",
                            code: currency,
                          })}
                            </Text>
                          </>
                        )}
                      </Flex>
                    </CustomTd>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Flex>
      </Collapse>
      {brandList.length !== 0 && (
        <Flex
          alignItems={"center"}
          gap={"4px"}
          cursor={"pointer"}
          onClick={() => setIsOpen((prev) => !prev)}
          justifyContent={"center"}
        >
          <Text textStyle={"body-sm-bold"} color={"#444444"}>
            {isOpen
              ? tTitle("order_history:brand_per_info.close_info")
              : tTitle("order_history:brand_per_info.show_info_per_brand")}
          </Text>
          <CustomIcon
            src={isOpen ? "chevron-up" : "chevron-down"}
            width={16}
            height={16}
          />
        </Flex>
      )}
    </Flex>
  );
};

type CustomThProps = React.ComponentProps<typeof Th>;
type CustomTdProps = React.ComponentProps<typeof Td>;

const CustomTh = ({ children, ...props }: CustomThProps) => {
  return (
    <Th padding={"10px 12px"} color={"#6F6F6C"} {...props}>
      <Text textStyle={"body-sm-medium"}>{children}</Text>
    </Th>
  );
};

const CustomTd = ({ children, ...props }: CustomTdProps) => {
  return (
    <Td padding={"10px 12px"} boxShadow={"0.5px 0 0 0 #D4D4D1"} {...props}>
      {children}
    </Td>
  );
};
